import React, { useEffect, useState } from 'react';

/**
 * Staged loader for the report.
 *
 * Each chapter costs the backend one aggregate scan over every lab result in the period, so a cold
 * request is genuinely slow — a static skeleton just looks stalled. This narrates the stages instead,
 * which is honest about what is happening and reads as progress rather than a hang.
 *
 * The messages advance on a timer, not off real backend events: the endpoint returns one payload, so
 * there is nothing finer to report. The bar therefore tracks stage position, not bytes.
 */

const METABOLIC_STAGES = [
  'Reading annual health-check records',
  'Grading lab results against reference ranges',
  'Counting abnormalities per employee',
  'Building the cohort',
];

const DEFICIENCY_STAGES = [
  'Reading vitamin, thyroid and haemoglobin results',
  'Working out how often deficiencies overlap',
  'Ranking by prevalence',
];

const STAGE_MS = 1900;

const ReportLoader = ({ stages = METABOLIC_STAGES, compact = false }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (stages.length < 2) return undefined;
    // Holds on the last stage rather than looping, so a slow request does not look like it restarted.
    const id = setInterval(() => {
      setIndex((current) => (current < stages.length - 1 ? current + 1 : current));
    }, STAGE_MS);
    return () => clearInterval(id);
  }, [stages]);

  const progress = ((index + 1) / stages.length) * 100;

  return (
    <div
      className={`flex flex-col items-center justify-center text-center ${compact ? 'py-ds-8' : 'py-ds-10'}`}
      role='status'
      aria-live='polite'
    >
      <span className='h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-ds-brand' />

      {/* Keyed so each message remounts and replays the DS entry animation, which already respects
          prefers-reduced-motion. */}
      <div className='mt-ds-5 h-[26px] overflow-hidden'>
        <div key={index} className='ds-anim-drop-in text-ts-md font-medium text-gray-800'>
          {`${stages[index]}…`}
        </div>
      </div>

      <div className='mt-ds-4 h-[3px] w-[220px] overflow-hidden rounded-ds-pill bg-gray-200'>
        <div
          className='h-full rounded-ds-pill bg-ds-brand transition-[width] duration-700 ease-out'
          style={{ width: `${progress}%` }}
        />
      </div>

      <p className='mt-ds-3 m-0 text-ts-xs text-gray-600'>
        Large workforces can take a few seconds on the first load
      </p>
    </div>
  );
};

export { METABOLIC_STAGES, DEFICIENCY_STAGES };
export default ReportLoader;
